import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemContent,
  Stack,
  Typography,
} from '@mui/joy';
import { FaDownload, FaPlay, FaStop } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import RecordingPlayer from '../components/RecordingPlayer';
import { fetchRecordingAudio, isRecordingAudioUnavailable, loadPersistedRecordings } from '../elevate/recordings.client';
import {
  formatDuration,
  formatLondonDateTime,
  recordingMatchesPhones,
  type NormalizedRecording,
} from '../elevate/recordings.model';

type Props = {
  customerName: string;
  phones: Array<string | null | undefined>;
};

function directionLabel(direction: NormalizedRecording['direction']): string {
  if (direction === 'incoming') return 'Incoming';
  if (direction === 'outgoing') return 'Outgoing';
  return 'Unknown';
}

export default function CustomerCallRecordingsCard({ customerName, phones }: Props) {
  const blobUrlRef = useRef<string | null>(null);
  const [rows, setRows] = useState<NormalizedRecording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<{ key: string; url: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const persisted = await loadPersistedRecordings();
        const matched = persisted
          .filter((row) => recordingMatchesPhones(row, phones))
          .sort((left, right) => Date.parse(right.startedAt || '') - Date.parse(left.startedAt || ''));
        if (!cancelled) setRows(matched);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load call recordings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [customerName, phones.join('|')]);

  const stop = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setActive(null);
  };

  const playRow = async (row: NormalizedRecording) => {
    const key = `${row.ownerUserId}:${row.recordingId}`;
    if (active?.key === key) {
      stop();
      return;
    }
    setBusyId(key);
    try {
      const blob = await fetchRecordingAudio(row.ownerUserId, row.recordingId);
      stop();
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setActive({ key, url });
    } catch (err) {
      const typed = err as Error & { code?: string };
      if (isRecordingAudioUnavailable(typed.code)) {
        setUnavailable((current) => ({ ...current, [key]: typed.message }));
      } else {
        setError(typed.message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const downloadRow = async (row: NormalizedRecording) => {
    const key = `${row.ownerUserId}:${row.recordingId}`;
    setBusyId(key);
    try {
      const blob = await fetchRecordingAudio(row.ownerUserId, row.recordingId, true);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `elevate-recording-${row.recordingId}.mp3`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const typed = err as Error & { code?: string };
      if (isRecordingAudioUnavailable(typed.code)) {
        setUnavailable((current) => ({ ...current, [key]: typed.message }));
      } else {
        setError(typed.message);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography level="h4" sx={{ color: '#111' }}>
            Call Recordings
          </Typography>
          <Button component={Link} to="/call-recordings" size="sm" variant="outlined">
            All recordings
          </Button>
        </Box>
        {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size="lg" />
          </Box>
        ) : rows.length === 0 ? (
          <Alert color="neutral">
            No recorded calls found for {customerName} yet. Sync Call Recordings to store the latest metadata.
          </Alert>
        ) : (
          <List>
            {rows.map((row) => {
              const key = `${row.ownerUserId}:${row.recordingId}`;
              const open = active?.key === key;
              return (
                <ListItem
                  key={key}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': { borderColor: 'primary.500', backgroundColor: 'background.level1' },
                  }}
                >
                  <ListItemContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography level="title-md" sx={{ mb: 0.5 }}>
                          {formatLondonDateTime(row.startedAt)}
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                          {row.ownerDisplayName} · {formatDuration(row.durationSeconds)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="sm" variant="soft">
                          {directionLabel(row.direction)}
                        </Chip>
                        {unavailable[key] ? (
                          <Typography level="body-xs" sx={{ color: 'danger.600' }}>{unavailable[key]}</Typography>
                        ) : (
                          <>
                            <IconButton
                              size="sm"
                              variant="plain"
                              aria-label={open ? 'Close player' : 'Play recording'}
                              loading={busyId === key}
                              onClick={() => void playRow(row)}
                            >
                              <IconWrapper icon={open ? FaStop : FaPlay} />
                            </IconButton>
                            <IconButton
                              size="sm"
                              variant="plain"
                              aria-label="Download recording"
                              loading={busyId === key}
                              onClick={() => void downloadRow(row)}
                            >
                              <IconWrapper icon={FaDownload} />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </Box>
                    {open && active && (
                      <Box sx={{ mt: 1 }}>
                        <RecordingPlayer src={active.url} fallbackDurationSeconds={row.durationSeconds} />
                      </Box>
                    )}
                  </ListItemContent>
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
